/**
 * Chain Configuration - Single Source of Truth
 *
 * GUIDELINES:
 * - If a chain is supported by viem/chains, import and use it directly
 * - If a chain is NOT in viem/chains, define a custom constant following the pattern below
 * - All chains should be added to VIEM_CHAINS map for centralized access
 */

// Viem built-in chains
import {
  mainnet,
  sepolia,
  arbitrum,
  arbitrumSepolia,
  apeChain,
  curtis,
  base,
  baseSepolia,
  abstract,
  abstractTestnet,
} from 'viem/chains';

// Re-export viem chains for convenience
export {
  mainnet,
  sepolia,
  arbitrum,
  arbitrumSepolia,
  apeChain,
  curtis,
  base,
  baseSepolia,
  abstract,
  abstractTestnet,
};

// Custom chain definitions (not available in viem/chains)
export const ENERGI_TESTNET = {
  id: 49797,
  name: 'Energi',
  network: 'energi',
  nativeCurrency: {
    name: 'Energi',
    symbol: 'NRG',
    decimals: 18,
  },
  rpcUrls: {
    public: { http: ['https://nodeapi.test.energi.network'] },
    default: { http: ['https://nodeapi.test.energi.network'] },
  },
  blockExplorers: {
    etherscan: { name: 'Energi explorer', url: 'https://explorer.test.energi.network' },
    default: { name: 'Energi explorer', url: 'https://explorer.test.energi.network' },
  },
  contracts: {
    multicall3: {
      address: '0x9510e36CAe130B28184B1aD3852FE5F67193c341',
      blockCreated: 2_441_944,
    },
  },
};

export const ENERGI_MAINNET = {
  id: 39797,
  name: 'Energi',
  network: 'energi',
  nativeCurrency: {
    name: 'Energi',
    symbol: 'NRG',
    decimals: 18,
  },
  rpcUrls: {
    public: { http: ['https://nodeapi.energi.network'] },
    default: { http: ['https://nodeapi.energi.network'] },
  },
  blockExplorers: {
    etherscan: { name: 'Energi explorer', url: 'https://explorer.energi.network' },
    default: { name: 'Energi explorer', url: 'https://explorer.energi.network' },
  },
  contracts: {},
};

export const NETWORK_RPC_URLS = {
  1: 'https://ethereum.energi.network',
  11155111: 'https://eth-sepolia.public.blastapi.io', // Ethereum Sepolia
  39797: 'https://nodeapi.energi.network',
  49797: 'https://nodeapi.test.energi.network',
  33139: 'https://apechain.calderachain.xyz/http',
  33111: 'https://curtis.rpc.caldera.xyz/http',
  42161: 'https://arb1.arbitrum.io/rpc', // Arbitrum Mainnet
  421614: 'https://sepolia-rollup.arbitrum.io/rpc', // Arbitrum Sepolia Testnet
  143: 'https://rpc.monad.xyz', // Monad Mainnet
  10143: 'https://testnet-rpc.monad.xyz', // Monad Testnet
  8453: 'https://base-rpc.publicnode.com', // Base Mainnet (PublicNode)
  84532: 'https://base-sepolia-rpc.publicnode.com', // Base Sepolia (PublicNode)
  2741: 'https://api.mainnet.abs.xyz', // Abstract Mainnet
  11124: 'https://api.testnet.abs.xyz', // Abstract Testnet
  56: 'https://bsc-mainnet.public.blastapi.io', // Binance Mainnet
  97: 'https://bsc-testnet.drpc.org', // Binance Testnet
};

export const ARBITRUM_MAINNET = {
  id: 42161,
  name: 'Arbitrum One',
  network: 'arbitrum',
  nativeCurrency: {
    name: 'Ether',
    symbol: 'ETH',
    decimals: 18,
  },
  rpcUrls: {
    public: { http: ['https://arb1.arbitrum.io/rpc'] },
    default: { http: ['https://arb1.arbitrum.io/rpc'] },
  },
  blockExplorers: {
    etherscan: { name: 'Arbiscan', url: 'https://arbiscan.io' },
    default: { name: 'Arbiscan', url: 'https://arbiscan.io' },
  },
  contracts: {},
};

export const ARBITRUM_TESTNET = {
  id: 421614,
  name: 'Arbitrum Sepolia',
  network: 'arbitrum-sepolia',
  nativeCurrency: {
    name: 'Ether',
    symbol: 'ETH',
    decimals: 18,
  },
  rpcUrls: {
    public: { http: ['https://sepolia-rollup.arbitrum.io/rpc'] },
    default: { http: ['https://sepolia-rollup.arbitrum.io/rpc'] },
  },
  blockExplorers: {
    etherscan: { name: 'Arbiscan Sepolia', url: 'https://sepolia.arbiscan.io' },
    default: { name: 'Arbiscan Sepolia', url: 'https://sepolia.arbiscan.io' },
  },
  contracts: {},
};

export const MONAD_MAINNET = {
  id: 143,
  name: 'Monad',
  network: 'monad',
  nativeCurrency: {
    name: 'Monad',
    symbol: 'MON',
    decimals: 18,
  },
  rpcUrls: {
    public: { http: ['https://rpc.monad.xyz'] },
    default: { http: ['https://rpc.monad.xyz'] },
  },
  blockExplorers: {
    etherscan: { name: 'Monadscan', url: 'https://monadscan.com' },
    default: { name: 'Monadscan', url: 'https://monadscan.com' },
  },
  contracts: {},
};

export const MONAD_TESTNET = {
  id: 10143,
  name: 'Monad Testnet',
  network: 'monad-testnet',
  nativeCurrency: {
    name: 'Monad',
    symbol: 'MON',
    decimals: 18,
  },
  rpcUrls: {
    public: { http: ['https://testnet-rpc.monad.xyz'] },
    default: { http: ['https://testnet-rpc.monad.xyz'] },
  },
  blockExplorers: {
    etherscan: { name: 'Monad Testnet Explorer', url: 'https://testnet.monadscan.com' },
    default: { name: 'Monad Testnet Explorer', url: 'https://testnet.monadscan.com' },
  },
  contracts: {},
};

export const BINANCE_MAINNET = {
  id: 56,
  name: 'Binance',
  network: 'binance',
  nativeCurrency: {
    name: 'Binance Coin',
    symbol: 'BNB',
    decimals: 18,
  },
  rpcUrls: {
    public: { http: ['https://bsc-mainnet.public.blastapi.io'] },
    default: { http: ['https://bsc-mainnet.public.blastapi.io'] },
  },
  blockExplorers: {
    etherscan: { name: 'BSCScan', url: 'https://bscscan.com' },
    default: { name: 'BSCScan', url: 'https://bscscan.com' },
  },
  contracts: {},
};

export const BINANCE_TESTNET = {
  id: 97,
  name: 'Binance Testnet',
  network: 'binance-testnet',
  nativeCurrency: {
    name: 'Binance Coin',
    symbol: 'BNB',
    decimals: 18,
  },
  rpcUrls: {
    public: { http: ['https://bsc-testnet.drpc.org'] },
    default: { http: ['https://bsc-testnet.drpc.org'] },
  },
  blockExplorers: {
    etherscan: { name: 'BSCScan Testnet', url: 'https://testnet.bscscan.com' },
    default: { name: 'BSCScan Testnet', url: 'https://testnet.bscscan.com' },
  },
  contracts: {},
};
/**
 * Central map of all supported chains: chainId → viem chain object
 * Combines viem built-in chains with custom chain definitions
 */
export const VIEM_CHAINS = {
  // Viem built-in chains
  [mainnet.id]: mainnet,
  [sepolia.id]: sepolia,
  [arbitrum.id]: arbitrum,
  [arbitrumSepolia.id]: arbitrumSepolia,
  [apeChain.id]: apeChain,
  [curtis.id]: curtis,
  [base.id]: base,
  [baseSepolia.id]: baseSepolia,
  [abstract.id]: abstract,
  [abstractTestnet.id]: abstractTestnet,
  // Custom chains (not in viem/chains)
  [ENERGI_MAINNET.id]: ENERGI_MAINNET,
  [ENERGI_TESTNET.id]: ENERGI_TESTNET,
  [MONAD_MAINNET.id]: MONAD_MAINNET,
  [MONAD_TESTNET.id]: MONAD_TESTNET,
  [BINANCE_MAINNET.id]: BINANCE_MAINNET,
  [BINANCE_TESTNET.id]: BINANCE_TESTNET,
};

/**
 * Get viem chain object by chain ID
 * @param {number} chainId - The chain ID to look up
 * @returns {object} The viem chain object
 * @throws {Error} If chain ID is not supported
 */
export const getViemChain = (chainId) => {
  const chain = VIEM_CHAINS[chainId];
  if (!chain) {
    const supportedIds = Object.keys(VIEM_CHAINS).join(', ');
    throw new Error(`Unsupported chain ID: ${chainId}. Supported: ${supportedIds}`);
  }
  return chain;
};
